import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  registerFcmToken,
  requestNotificationPermission,
  subscribeForegroundMessages,
} from '../pwa/fcmService';
import { isStandaloneDisplayMode } from '../pwa/installPrompt';

const PROMPT_DELAY_MS = 3500;

function promptKey(uid) {
  return `lockedin_fcm_prompted_${uid}`;
}

/**
 * Request notification permission after login (not first anonymous visit).
 * Stores FCM token on users/{uid}.fcmToken.
 */
export function usePushNotifications({ enabled = false, uid = null } = {}) {
  useEffect(() => {
    if (!enabled || !uid || Capacitor.isNativePlatform()) return undefined;

    let cancelled = false;

    const setup = async () => {
      await new Promise((r) => setTimeout(r, PROMPT_DELAY_MS));
      if (cancelled) return;

      if (Notification.permission === 'granted') {
        await registerFcmToken(uid);
        return;
      }
      if (Notification.permission === 'denied') return;

      const alreadyPrompted = window.localStorage.getItem(promptKey(uid)) === '1';
      if (alreadyPrompted) return;

      const granted = await requestNotificationPermission();
      window.localStorage.setItem(promptKey(uid), '1');
      if (granted && !cancelled) {
        await registerFcmToken(uid);
      }
    };

    setup();

    return () => {
      cancelled = true;
    };
  }, [enabled, uid]);

  useEffect(() => {
    if (!enabled || !uid) return undefined;

    let unsub = () => {};
    subscribeForegroundMessages((payload) => {
      const title = payload?.notification?.title || 'LockedIn';
      const body = payload?.notification?.body || '';
      if (Notification.permission === 'granted' && document.visibilityState === 'visible') {
        try {
          new Notification(title, {
            body,
            icon: `${process.env.PUBLIC_URL || ''}/icons/icon-192.png`,
            tag: payload?.data?.type || 'lockedin',
          });
        } catch (_) {}
      }
    }).then((off) => {
      unsub = off || (() => {});
    });

    return () => unsub();
  }, [enabled, uid]);

  useEffect(() => {
    if (isStandaloneDisplayMode() && uid && enabled) {
      registerFcmToken(uid);
    }
  }, [enabled, uid]);
}
